import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { PieChart as PieChartIcon, BarChart as BarChartIcon } from '@mui/icons-material';

// Custom scrollable legend component for histograms
const ScrollableLegend = (props: any) => {
  const { payload, onToggle, hiddenItems, onToggleAll } = props;
  if (!payload || payload.length === 0) return null;

  const someHidden = payload.some((entry: any) => hiddenItems?.has(entry.value));
  const SELECT_ALL_LABEL = 'Select All';

  return (
    <Box
      sx={{
        maxHeight: '80px',
        overflowY: 'auto',
        overflowX: 'hidden',
        px: 2,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: '#555',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', py: 1 }}>
        {/* Select All option */}
        <Box
          onClick={onToggleAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.75rem',
            fontWeight: 700,
            cursor: 'pointer',
            opacity: someHidden ? 0.3 : 1,
            textDecoration: someHidden ? 'line-through' : 'none',
            transition: 'opacity 0.2s',
            borderBottom: '2px solid #0067b3',
            pb: 0.5,
            '&:hover': {
              opacity: someHidden ? 0.5 : 0.8,
            },
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              background: 'linear-gradient(135deg, #0067b3 25%, #42a5f5 25%, #42a5f5 50%, #0067b3 50%, #0067b3 75%, #42a5f5 75%, #42a5f5)',
              borderRadius: '2px',
            }}
          />
          <span>{SELECT_ALL_LABEL}</span>
        </Box>
        {payload.map((entry: any, index: number) => {
          const isHidden = hiddenItems?.has(entry.value);
          return (
            <Box
              key={`legend-${index}`}
              onClick={() => onToggle?.(entry.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
                cursor: 'pointer',
                opacity: isHidden ? 0.3 : 1,
                textDecoration: isHidden ? 'line-through' : 'none',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: isHidden ? 0.5 : 0.8,
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '2px',
                }}
              />
              <span>{entry.value}</span>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Custom legend for pie charts (at bottom)
const PieLegend = (props: any) => {
  const { payload, onToggle, hiddenItems, onToggleAll } = props;
  if (!payload || payload.length === 0) return null;

  const someHidden = payload.some((entry: any) => hiddenItems?.has(entry.payload.name));
  const SELECT_ALL_LABEL = 'Select All';

  return (
    <Box
      sx={{
        maxHeight: '100px',
        overflowY: 'auto',
        overflowX: 'hidden',
        px: 2,
        mt: 1,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '3px',
          '&:hover': {
            backgroundColor: '#555',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', py: 1 }}>
        {/* Select All option */}
        <Box
          onClick={onToggleAll}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.75rem',
            fontWeight: 700,
            cursor: 'pointer',
            opacity: someHidden ? 0.3 : 1,
            textDecoration: someHidden ? 'line-through' : 'none',
            transition: 'opacity 0.2s',
            borderBottom: '2px solid #0067b3',
            pb: 0.5,
            '&:hover': {
              opacity: someHidden ? 0.5 : 0.8,
            },
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              background: 'linear-gradient(135deg, #0067b3 25%, #42a5f5 25%, #42a5f5 50%, #0067b3 50%, #0067b3 75%, #42a5f5 75%, #42a5f5)',
              borderRadius: '2px',
            }}
          />
          <span>{SELECT_ALL_LABEL}</span>
        </Box>
        {payload.map((entry: any, index: number) => {
          const isHidden = hiddenItems?.has(entry.payload.name);
          return (
            <Box
              key={`legend-${index}`}
              onClick={() => onToggle?.(entry.payload.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
                cursor: 'pointer',
                opacity: isHidden ? 0.3 : 1,
                textDecoration: isHidden ? 'line-through' : 'none',
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: isHidden ? 0.5 : 0.8,
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '2px',
                }}
              />
              <span>{entry.payload.name}: {entry.payload.value}</span>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

interface ResolutionBucket {
  period: string;
  managedByNOC: number;
  mttrManagedByNOC: number;
  resolvedByNOC: number;
  mttrResolvedByNOC: number;
  resolvedByOther: number;
  mttrResolvedByOther: number;
  cancelled: number;
  mttrCancelled: number;
  p90TTR: number;
}

interface ResolutionCodeSplit {
  period: string;
  codes: { [key: string]: number };
}

interface LocationSplit {
  period: string;
  locations: { [key: string]: number };
}

interface CINameSplit {
  period: string;
  ciNames: { [key: string]: number };
}

interface CIClassSplit {
  period: string;
  ciClasses: { [key: string]: number };
}

interface MTTRBucket {
  period: string;
  buckets: {
    '0-10 mins': number;
    '10-30 mins': number;
    '30 mins - 24 hours': number;
    '24 - 48 hours': number;
    '48 - 72 hours': number;
    '>72 hours': number;
  };
}

interface StateAndHoldReason {
  period: string;
  stateReasons: {
    [state: string]: {
      count: number;
      holdReasons: { [reason: string]: number };
    };
  };
}

interface NOCTransfer {
  period: string;
  startedAtNOC: number;
  sentOut: number;
  percentageSentOut: number;
  solvedByAutomation: number;
}

interface OBSIncident {
  period: string;
  count: number;
}

interface NOCDashboardVisualProps {
  resolutionBuckets: ResolutionBucket[];
  resolutionCodeSplit: ResolutionCodeSplit[];
  locationSplit: LocationSplit[];
  ciNameSplit: CINameSplit[];
  ciClassSplit: CIClassSplit[];
  mttrBuckets: MTTRBucket[];
  stateAndHoldReasons: StateAndHoldReason[];
  nocTransfers: {
    timeSeries: NOCTransfer[];
    total: number;
    totalSentOut: number;
    overallPercentage: number;
    totalSolvedByAutomation: number;
  };
  obsIncidents: {
    timeSeries: OBSIncident[];
    total: number;
  };
  loading: boolean;
  selectedLocations: string[];
}

// Color palettes
const CATEGORY_COLORS = ['#0067b3', '#42a5f5', '#66bb6a', '#ffa726'];
const RESOLUTION_CODE_COLORS = [
  '#0067b3', '#1976d2', '#42a5f5', '#64b5f6', '#90caf9',
  '#2196f3', '#03a9f4', '#00bcd4', '#26c6da', '#00acc1',
  '#0097a7', '#00838f', '#006064', '#1de9b6', '#00e676',
];
const STATE_COLORS = [
  '#0067b3', '#1976d2', '#42a5f5', '#64b5f6', '#90caf9',
  '#bbdefb', '#e3f2fd', '#0d47a1', '#1565c0', '#1e88e5',
];
const MTTR_COLORS = ['#4caf50', '#ffc107', '#ff9800', '#f44336'];
const TRANSFER_COLORS = ['#0067b3', '#42a5f5', '#66bb6a'];

const INCIDENT_CATEGORY_KEYS = ['Created by NOC', 'Resolved by NOC', 'Resolved by Other', 'Cancelled'] as const;
const INCIDENT_CATEGORY_COLOR_MAP: Record<string, string> = {
  'Created by NOC': CATEGORY_COLORS[0],
  'Resolved by NOC': CATEGORY_COLORS[1],
  'Resolved by Other': CATEGORY_COLORS[2],
  'Cancelled': CATEGORY_COLORS[3],
};

const MTTR_CATEGORY_KEYS = ['0-10 mins', '10-30 mins', '30 mins - 24 hours', '24 - 48 hours', '48 - 72 hours', '>72 hours'] as const;
const MTTR_CATEGORY_COLOR_MAP: Record<string, string> = {
  '0-10 mins': '#4caf50',
  '10-30 mins': '#8bc34a',
  '30 mins - 24 hours': '#ffc107',
  '24 - 48 hours': '#ff9800',
  '48 - 72 hours': '#ff5722',
  '>72 hours': '#f44336',
};

const TRANSFER_CATEGORY_KEYS = [
  'Started at NOC',
  'Solved by automation',
  'Sent Out',
] as const;
const TRANSFER_CATEGORY_COLOR_MAP: Record<string, string> = {
  'Started at NOC': TRANSFER_COLORS[0],
  'Solved by automation': TRANSFER_COLORS[1],
  'Sent Out': TRANSFER_COLORS[2],
};

const OBS_CATEGORY_KEYS = ['OBS Count'] as const;
const OBS_CATEGORY_COLOR_MAP: Record<string, string> = {
  'OBS Count': '#0067b3',
};

type LegendTuple = [string, number];
const sortEntriesByValueDesc = (entries: LegendTuple[]): LegendTuple[] =>
  [...entries].sort((a, b) => {
    if (b[1] !== a[1]) {
      return b[1] - a[1];
    }
    return a[0].localeCompare(b[0]);
  });

const NOCDashboardVisual: React.FC<NOCDashboardVisualProps> = ({
  resolutionBuckets,
  resolutionCodeSplit,
  locationSplit,
  ciNameSplit,
  ciClassSplit,
  mttrBuckets,
  stateAndHoldReasons,
  nocTransfers,
  obsIncidents,
  loading,
  selectedLocations,
}) => {
  // Chart type states (histogram or pie)
  const [chart1Type, setChart1Type] = useState<'histogram' | 'pie'>('histogram');
  const [chart2Type, setChart2Type] = useState<'histogram' | 'pie'>('histogram');
  const [chart2aType, setChart2aType] = useState<'histogram' | 'pie'>('histogram');
  const [chart3Type, setChart3Type] = useState<'histogram' | 'pie'>('histogram');
  const [chart4Type, setChart4Type] = useState<'histogram' | 'pie'>('histogram');
  const [chart5Type, setChart5Type] = useState<'histogram' | 'pie'>('histogram');
  const [chart10Type, setChart10Type] = useState<'histogram' | 'pie'>('histogram');

  // Hidden legend items for each chart
  const [chart1Hidden, setChart1Hidden] = useState<Set<string>>(new Set());
  const [chart2Hidden, setChart2Hidden] = useState<Set<string>>(new Set());
  const [chart2aHidden, setChart2aHidden] = useState<Set<string>>(new Set());
  const [chart3Hidden, setChart3Hidden] = useState<Set<string>>(new Set());
  const [chart4Hidden, setChart4Hidden] = useState<Set<string>>(new Set());
  const [chart5Hidden, setChart5Hidden] = useState<Set<string>>(new Set());
  const [chart6Hidden, setChart6Hidden] = useState<Set<string>>(new Set());
  const [chart7Hidden, setChart7Hidden] = useState<Set<string>>(new Set());
  const [chart8Hidden, setChart8Hidden] = useState<Set<string>>(new Set());
  const [chart10Hidden, setChart10Hidden] = useState<Set<string>>(new Set());

  // Toggle legend visibility
  const toggleLegendItem = (setHidden: React.Dispatch<React.SetStateAction<Set<string>>>, item: string) => {
    setHidden((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(item)) {
        newSet.delete(item);
      } else {
        newSet.add(item);
      }
      return newSet;
    });
  };

  // Toggle all legend items
  const toggleAllLegendItems = (setHidden: React.Dispatch<React.SetStateAction<Set<string>>>, allItems: string[]) => {
    setHidden((prev) => {
      // If any item is visible, hide all. If all are hidden, show all.
      const allHidden = allItems.every(item => prev.has(item));
      if (allHidden) {
        return new Set();
      } else {
        return new Set(allItems);
      }
    });
  };

  // Transform data for Chart 1: Incidents by Category
  const chart1Data = useMemo(() => {
    return resolutionBuckets.map((bucket) => ({
      period: bucket.period,
      'Managed by NOC (All)': bucket.managedByNOC,
      'Resolved by NOC': bucket.resolvedByNOC,
      'Resolved by Other': bucket.resolvedByOther,
      'Cancelled': bucket.cancelled,
    }));
  }, [resolutionBuckets]);

  const chart1TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {
      'Managed by NOC (All)': resolutionBuckets.reduce((sum, b) => sum + b.managedByNOC, 0),
      'Resolved by NOC': resolutionBuckets.reduce((sum, b) => sum + b.resolvedByNOC, 0),
      'Resolved by Other': resolutionBuckets.reduce((sum, b) => sum + b.resolvedByOther, 0),
      'Cancelled': resolutionBuckets.reduce((sum, b) => sum + b.cancelled, 0),
    };
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [resolutionBuckets]);

  const chart1PieData = useMemo(() => {
    return chart1TotalsEntries
      .filter(([name]) => !chart1Hidden.has(name) && ['Resolved by NOC', 'Resolved by Other', 'Cancelled'].includes(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart1TotalsEntries, chart1Hidden]);

  const chart1LegendPayload = useMemo(() => {
    return chart1TotalsEntries.map(([name, value]) => ({
      value: name,
      color: INCIDENT_CATEGORY_COLOR_MAP[name] ?? CATEGORY_COLORS[0],
      payload: { name, value },
    }));
  }, [chart1TotalsEntries]);

  const chart1PieLegendPayload = useMemo(() => {
    const pieCategories = ['Resolved by NOC', 'Resolved by Other', 'Cancelled'];
    return pieCategories.map((name) => ({
      value: name,
      color: INCIDENT_CATEGORY_COLOR_MAP[name] ?? CATEGORY_COLORS[0],
      payload: { name },
    }));
  }, []);

  const chart1SeriesKeys = useMemo(() => chart1TotalsEntries.map(([name]) => name), [chart1TotalsEntries]);

  // Transform data for Chart 2: Resolution Code Split
  const chart2Data = useMemo(() => {
    return resolutionCodeSplit.map((split) => ({
      period: split.period,
      ...split.codes,
    }));
  }, [resolutionCodeSplit]);

  const allResolutionCodes = useMemo(() => {
    const codes = new Set<string>();
    resolutionCodeSplit.forEach((split) => {
      Object.keys(split.codes).forEach((code) => codes.add(code));
    });
    return Array.from(codes).sort();
  }, [resolutionCodeSplit]);

  const resolutionCodeColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    allResolutionCodes.forEach((code, index) => {
      map[code] = RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length];
    });
    return map;
  }, [allResolutionCodes]);

  const chart2TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {};
    resolutionCodeSplit.forEach((split) => {
      Object.entries(split.codes).forEach(([code, count]) => {
        totals[code] = (totals[code] || 0) + count;
      });
    });
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [resolutionCodeSplit]);

  const chart2PieData = useMemo(() => {
    return chart2TotalsEntries
      .filter(([name]) => !chart2Hidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart2TotalsEntries, chart2Hidden]);

  const chart2LegendPayload = useMemo(() => {
    return chart2TotalsEntries.map(([name, value]) => ({
      value: name,
      color: resolutionCodeColorMap[name] || RESOLUTION_CODE_COLORS[0],
      payload: { name, value },
    }));
  }, [chart2TotalsEntries, resolutionCodeColorMap]);

  const chart2SeriesKeys = useMemo(() => chart2TotalsEntries.map(([name]) => name), [chart2TotalsEntries]);

  // Transform data for Chart 2a: Location Split
  const chart2aData = useMemo(() => {
    return locationSplit.map((split) => ({
      period: split.period,
      ...split.locations,
    }));
  }, [locationSplit]);

  const allLocations = useMemo(() => {
    const locations = new Set<string>();
    locationSplit.forEach((split) => {
      Object.keys(split.locations).forEach((location) => locations.add(location));
    });
    return Array.from(locations).sort();
  }, [locationSplit]);

  const locationColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    allLocations.forEach((location, index) => {
      map[location] = RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length];
    });
    return map;
  }, [allLocations]);

  const chart2aTotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {};
    locationSplit.forEach((split) => {
      Object.entries(split.locations).forEach(([location, count]) => {
        totals[location] = (totals[location] || 0) + count;
      });
    });
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [locationSplit]);

  const chart2aPieData = useMemo(() => {
    return chart2aTotalsEntries
      .filter(([name]) => !chart2aHidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart2aTotalsEntries, chart2aHidden]);

  const chart2aLegendPayload = useMemo(() => {
    return chart2aTotalsEntries.map(([name, value]) => ({
      value: name,
      color: locationColorMap[name] || RESOLUTION_CODE_COLORS[0],
      payload: { name, value },
    }));
  }, [chart2aTotalsEntries, locationColorMap]);

  const chart2aSeriesKeys = useMemo(() => chart2aTotalsEntries.map(([name]) => name), [chart2aTotalsEntries]);

  const chart2aPieLegendPayload = useMemo(() => {
    return chart2aTotalsEntries.map(([name, value]) => ({
      value: name,
      color: locationColorMap[name] || RESOLUTION_CODE_COLORS[0],
      payload: { name, value },
    }));
  }, [chart2aTotalsEntries, locationColorMap]);

  // Transform data for Chart 3: State Split
  const chart3Data = useMemo(() => {
    return stateAndHoldReasons.map((item) => {
      const stateData: any = { period: item.period };
      Object.entries(item.stateReasons).forEach(([state, data]) => {
        stateData[state] = data.count || 0;
      });
      return stateData;
    });
  }, [stateAndHoldReasons]);

  const allStates = useMemo(() => {
    const states = new Set<string>();
    stateAndHoldReasons.forEach((item) => {
      Object.keys(item.stateReasons).forEach((state) => states.add(state));
    });
    return Array.from(states).sort();
  }, [stateAndHoldReasons]);

  const stateColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    allStates.forEach((state, index) => {
      map[state] = STATE_COLORS[index % STATE_COLORS.length];
    });
    return map;
  }, [allStates]);

  const chart3TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {};
    stateAndHoldReasons.forEach((item) => {
      Object.entries(item.stateReasons).forEach(([state, stateData]) => {
        totals[state] = (totals[state] || 0) + (stateData.count || 0);
      });
    });
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [stateAndHoldReasons]);

  const chart3PieData = useMemo(() => {
    return chart3TotalsEntries
      .filter(([name]) => !chart3Hidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart3TotalsEntries, chart3Hidden]);

  const chart3LegendPayload = useMemo(() => {
    return chart3TotalsEntries.map(([name, value]) => ({
      value: name,
      color: stateColorMap[name] || STATE_COLORS[0],
      payload: { name, value },
    }));
  }, [chart3TotalsEntries, stateColorMap]);

  const chart3SeriesKeys = useMemo(() => chart3TotalsEntries.map(([name]) => name), [chart3TotalsEntries]);

  // Transform data for Chart 4: MTTR Distribution
  const chart4Data = useMemo(() => {
    return mttrBuckets.map((bucket) => ({
      period: bucket.period,
      '0-10 mins': bucket.buckets['0-10 mins'],
      '10-30 mins': bucket.buckets['10-30 mins'],
      '30 mins - 24 hours': bucket.buckets['30 mins - 24 hours'],
      '24 - 48 hours': bucket.buckets['24 - 48 hours'],
      '48 - 72 hours': bucket.buckets['48 - 72 hours'],
      '>72 hours': bucket.buckets['>72 hours'],
    }));
  }, [mttrBuckets]);

  const chart4TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {
      '0-10 mins': mttrBuckets.reduce((sum, b) => sum + b.buckets['0-10 mins'], 0),
      '10-30 mins': mttrBuckets.reduce((sum, b) => sum + b.buckets['10-30 mins'], 0),
      '30 mins - 24 hours': mttrBuckets.reduce((sum, b) => sum + b.buckets['30 mins - 24 hours'], 0),
      '24 - 48 hours': mttrBuckets.reduce((sum, b) => sum + b.buckets['24 - 48 hours'], 0),
      '48 - 72 hours': mttrBuckets.reduce((sum, b) => sum + b.buckets['48 - 72 hours'], 0),
      '>72 hours': mttrBuckets.reduce((sum, b) => sum + b.buckets['>72 hours'], 0),
    };
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [mttrBuckets]);

  const chart4PieData = useMemo(() => {
    return chart4TotalsEntries
      .filter(([name]) => !chart4Hidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart4TotalsEntries, chart4Hidden]);

  const chart4LegendPayload = useMemo(() => {
    return chart4TotalsEntries.map(([name, value]) => ({
      value: name,
      color: MTTR_CATEGORY_COLOR_MAP[name] ?? MTTR_COLORS[0],
      payload: { name, value },
    }));
  }, [chart4TotalsEntries]);

  const chart4SeriesKeys = useMemo(() => chart4TotalsEntries.map(([name]) => name), [chart4TotalsEntries]);

  // Transform data for Chart 5: State and Hold Reasons (all states, not drill-down)
  const chart5Data = useMemo(() => {
    return stateAndHoldReasons.map((item) => {
      const stateData: any = { period: item.period };
      Object.entries(item.stateReasons).forEach(([state, data]) => {
        stateData[state] = data.count || 0;
      });
      return stateData;
    });
  }, [stateAndHoldReasons]);

  const allStateNames = useMemo(() => {
    const states = new Set<string>();
    stateAndHoldReasons.forEach((item) => {
      Object.keys(item.stateReasons).forEach((state) => states.add(state));
    });
    return Array.from(states).sort();
  }, [stateAndHoldReasons]);

  const holdStateColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    allStateNames.forEach((state, index) => {
      map[state] = STATE_COLORS[index % STATE_COLORS.length];
    });
    return map;
  }, [allStateNames]);

  const chart5TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {};
    stateAndHoldReasons.forEach((item) => {
      Object.entries(item.stateReasons).forEach(([state, stateData]) => {
        totals[state] = (totals[state] || 0) + (stateData.count || 0);
      });
    });
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [stateAndHoldReasons]);

  const chart5PieData = useMemo(() => {
    return chart5TotalsEntries
      .filter(([name]) => !chart5Hidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart5TotalsEntries, chart5Hidden]);

  const chart5LegendPayload = useMemo(() => {
    return chart5TotalsEntries.map(([name, value]) => ({
      value: name,
      color: holdStateColorMap[name] || STATE_COLORS[0],
      payload: { name, value },
    }));
  }, [chart5TotalsEntries, holdStateColorMap]);

  const chart5SeriesKeys = useMemo(() => chart5TotalsEntries.map(([name]) => name), [chart5TotalsEntries]);

  // Transform data for Chart 6: NOC Transfers (Started at NOC, Solved by automation, and Sent Out)
  const chart6Data = useMemo(() => {
    return nocTransfers.timeSeries.map((transfer) => ({
      period: transfer.period,
      'Started at NOC': transfer.startedAtNOC,
      'Solved by automation': transfer.solvedByAutomation,
      'Sent Out': transfer.sentOut,
    }));
  }, [nocTransfers]);

  const chart6TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {
      'Started at NOC': nocTransfers.total,
      'Solved by automation': nocTransfers.totalSolvedByAutomation,
      'Sent Out': nocTransfers.totalSentOut,
    };
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [nocTransfers]);

  const chart6LegendPayload = useMemo(() => {
    return chart6TotalsEntries.map(([name, value]) => ({
      value: name,
      color: TRANSFER_CATEGORY_COLOR_MAP[name] || TRANSFER_COLORS[0],
      payload: { name, value },
    }));
  }, [chart6TotalsEntries]);

  const chart6SeriesKeys = useMemo(() => chart6TotalsEntries.map(([name]) => name), [chart6TotalsEntries]);

  // Transform data for Chart 7: OBS Incidents
  const chart7Data = useMemo(() => {
    return obsIncidents.timeSeries.map((obs) => ({
      period: obs.period,
      'OBS Count': obs.count,
    }));
  }, [obsIncidents]);

  const chart7TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {
      'OBS Count': obsIncidents.total,
    };
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [obsIncidents]);

  const chart7PieData = useMemo(() => {
    return chart7TotalsEntries
      .filter(([name]) => !chart7Hidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart7TotalsEntries, chart7Hidden]);

  const chart7LegendPayload = useMemo(() => {
    return chart7TotalsEntries.map(([name, value]) => ({
      value: name,
      color: OBS_CATEGORY_COLOR_MAP[name] || '#0067b3',
      payload: { name, value },
    }));
  }, [chart7TotalsEntries]);

  const chart7SeriesKeys = useMemo(() => chart7TotalsEntries.map(([name]) => name), [chart7TotalsEntries]);

  // Transform data for Chart 8: MTTR by Category (Bar chart)
  const MTTR_CATEGORY_KEYS_CHART8 = [
    'Managed by NOC (All)',
    'Resolved by NOC',
    'Resolved by Other',
    'Cancelled',
    'P90 TTR (Resolved by NOC)',
  ] as const;

  const MTTR_CATEGORY_COLOR_MAP_CHART8: Record<string, string> = {
    'Managed by NOC (All)': '#0067b3',
    'Resolved by NOC': '#42a5f5',
    'Resolved by Other': '#66bb6a',
    'Cancelled': '#ffa726',
    'P90 TTR (Resolved by NOC)': '#ab47bc',
  };

  const chart8Data = useMemo(() => {
    return resolutionBuckets.map((bucket) => ({
      period: bucket.period,
      'Managed by NOC (All)': bucket.mttrManagedByNOC || 0,
      'Resolved by NOC': bucket.mttrResolvedByNOC || 0,
      'Resolved by Other': bucket.mttrResolvedByOther || 0,
      'Cancelled': bucket.mttrCancelled || 0,
      'P90 TTR (Resolved by NOC)': bucket.p90TTR || 0,
    }));
  }, [resolutionBuckets]);

  const chart8LegendPayload = useMemo(() => {
    return MTTR_CATEGORY_KEYS_CHART8.map((name) => ({
      value: name,
      color: MTTR_CATEGORY_COLOR_MAP_CHART8[name],
      payload: { name },
    }));
  }, []);

  const chart8SeriesKeys = useMemo(() => Array.from(MTTR_CATEGORY_KEYS_CHART8), []);

  // Transform data for Chart 10: CI Class Split
  const chart10Data = useMemo(() => {
    return ciClassSplit.map((split) => ({
      period: split.period,
      ...split.ciClasses,
    }));
  }, [ciClassSplit]);

  const allCIClasses = useMemo(() => {
    const classes = new Set<string>();
    ciClassSplit.forEach((split) => {
      Object.keys(split.ciClasses).forEach((cls) => classes.add(cls));
    });
    return Array.from(classes).sort();
  }, [ciClassSplit]);

  const ciClassColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    allCIClasses.forEach((cls, index) => {
      map[cls] = RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length];
    });
    return map;
  }, [allCIClasses]);

  const chart10TotalsEntries = useMemo(() => {
    const totals: Record<string, number> = {};
    ciClassSplit.forEach((split) => {
      Object.entries(split.ciClasses).forEach(([cls, count]) => {
        totals[cls] = (totals[cls] || 0) + count;
      });
    });
    return sortEntriesByValueDesc(Object.entries(totals));
  }, [ciClassSplit]);

  const chart10PieData = useMemo(() => {
    return chart10TotalsEntries
      .filter(([name]) => !chart10Hidden.has(name))
      .map(([name, value]) => ({ name, value }));
  }, [chart10TotalsEntries, chart10Hidden]);

  const chart10LegendPayload = useMemo(() => {
    return chart10TotalsEntries.map(([name, value]) => ({
      value: name,
      color: ciClassColorMap[name] || RESOLUTION_CODE_COLORS[0],
      payload: { name, value },
    }));
  }, [chart10TotalsEntries, ciClassColorMap]);

  const chart10SeriesKeys = useMemo(() => chart10TotalsEntries.map(([name]) => name), [chart10TotalsEntries]);

  // CSS containment style for each chart paper - tells the browser each chart
  // is an independent layout/paint boundary, preventing cross-chart reflows
  const chartPaperSx = { p: 2, height: 450, contain: 'content' as const };

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        {/* Chart 1: Incidents by Category */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Incidents by Category
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
              <ToggleButtonGroup
                value={chart1Type}
                exclusive
                onChange={(e, val) => val && setChart1Type(val)}
                size="small"
              >
                <ToggleButton value="histogram">
                  <Tooltip title="Histogram">
                    <BarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="pie">
                  <Tooltip title="Pie Chart">
                    <PieChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              {chart1Type === 'histogram' ? (
                <BarChart data={chart1Data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend
                    content={(legendProps) => (
                      <ScrollableLegend
                        {...legendProps}
                        payload={chart1LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart1Hidden, item)}
                        hiddenItems={chart1Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart1Hidden, chart1SeriesKeys)}
                      />
                    )}
                  />
                  {chart1SeriesKeys.map((key: string) => (
                    <Bar 
                      key={key}
                      dataKey={key} 
                      fill={INCIDENT_CATEGORY_COLOR_MAP[key] ?? CATEGORY_COLORS[0]} 
                      hide={chart1Hidden.has(key)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chart1PieData}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {chart1PieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={INCIDENT_CATEGORY_COLOR_MAP[entry.name] ?? CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend
                    content={(legendProps) => (
                      <PieLegend
                        {...legendProps}
                        payload={chart1PieLegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart1Hidden, item)}
                        hiddenItems={chart1Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart1Hidden, ['Resolved by NOC', 'Resolved by Other', 'Cancelled'])}
                      />
                    )}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 8: MTTR by Category */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  MTTR by Category (hours)
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chart8Data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <RechartsTooltip />
                <Legend
                  content={(legendProps) => (
                    <ScrollableLegend
                      {...legendProps}
                      payload={chart8LegendPayload}
                      onToggle={(item: string) => toggleLegendItem(setChart8Hidden, item)}
                      hiddenItems={chart8Hidden}
                      onToggleAll={() => toggleAllLegendItems(setChart8Hidden, chart8SeriesKeys)}
                    />
                  )}
                />
                {chart8SeriesKeys.map((key) => (
                  <Bar 
                    key={key}
                    dataKey={key} 
                    fill={MTTR_CATEGORY_COLOR_MAP_CHART8[key] || '#0067b3'} 
                    hide={chart8Hidden.has(key)}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 2: Resolution Code Split */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Resolution Code Split
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
              <ToggleButtonGroup
                value={chart2Type}
                exclusive
                onChange={(e, val) => val && setChart2Type(val)}
                size="small"
              >
                <ToggleButton value="histogram">
                  <Tooltip title="Histogram">
                    <BarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="pie">
                  <Tooltip title="Pie Chart">
                    <PieChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              {chart2Type === 'histogram' ? (
                <BarChart data={chart2Data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend
                    content={(legendProps) => (
                      <ScrollableLegend
                        {...legendProps}
                        payload={chart2LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart2Hidden, item)}
                        hiddenItems={chart2Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart2Hidden, chart2SeriesKeys)}
                      />
                    )}
                  />
                  {allResolutionCodes.map((code, index) => (
                    <Bar 
                      key={code} 
                      dataKey={code} 
                      fill={resolutionCodeColorMap[code] || RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length]} 
                      hide={chart2Hidden.has(code)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chart2PieData}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {chart2PieData.map((entry, index) => {
                      const fillColor = resolutionCodeColorMap[entry.name] || RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length];
                      return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                  </Pie>
                  <RechartsTooltip />
                  <Legend
                    verticalAlign="bottom"
                    content={(legendProps) => (
                      <PieLegend
                        {...legendProps}
                        payload={chart2LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart2Hidden, item)}
                        hiddenItems={chart2Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart2Hidden, chart2SeriesKeys)}
                      />
                    )}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 4: MTTR Distribution */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  MTTR Distribution
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
              <ToggleButtonGroup
                value={chart4Type}
                exclusive
                onChange={(e, val) => val && setChart4Type(val)}
                size="small"
              >
                <ToggleButton value="histogram">
                  <Tooltip title="Histogram">
                    <BarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="pie">
                  <Tooltip title="Pie Chart">
                    <PieChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              {chart4Type === 'histogram' ? (
                <BarChart data={chart4Data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend
                    content={(legendProps) => (
                      <ScrollableLegend
                        {...legendProps}
                        payload={chart4LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart4Hidden, item)}
                        hiddenItems={chart4Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart4Hidden, chart4SeriesKeys)}
                      />
                    )}
                  />
                  {chart4SeriesKeys.map((key) => (
                    <Bar 
                      key={key}
                      dataKey={key} 
                      fill={MTTR_CATEGORY_COLOR_MAP[key] ?? MTTR_COLORS[0]} 
                      hide={chart4Hidden.has(key)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chart4PieData}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {chart4PieData.map((entry, index) => {
                      const fillColor = MTTR_CATEGORY_COLOR_MAP[entry.name] ?? MTTR_COLORS[index % MTTR_COLORS.length];
                      return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                  </Pie>
                  <RechartsTooltip />
                  <Legend
                    verticalAlign="bottom"
                    content={(legendProps) => (
                      <PieLegend
                        {...legendProps}
                        payload={chart4LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart4Hidden, item)}
                        hiddenItems={chart4Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart4Hidden, chart4SeriesKeys)}
                      />
                    )}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 5: States (without drill-down to hold reasons) */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Incidents by State
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
              <ToggleButtonGroup
                value={chart5Type}
                exclusive
                onChange={(e, val) => val && setChart5Type(val)}
                size="small"
              >
                <ToggleButton value="histogram">
                  <Tooltip title="Histogram">
                    <BarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="pie">
                  <Tooltip title="Pie Chart">
                    <PieChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              {chart5Type === 'histogram' ? (
                <BarChart data={chart5Data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend
                    content={(legendProps) => (
                      <ScrollableLegend
                        {...legendProps}
                        payload={chart5LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart5Hidden, item)}
                        hiddenItems={chart5Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart5Hidden, chart5SeriesKeys)}
                      />
                    )}
                  />
                  {allStateNames.map((state, index) => (
                    <Bar
                      key={state}
                      dataKey={state}
                      fill={holdStateColorMap[state] || STATE_COLORS[index % STATE_COLORS.length]}
                      hide={chart5Hidden.has(state)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chart5PieData}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {chart5PieData.map((entry, index) => {
                      const fillColor = holdStateColorMap[entry.name] || STATE_COLORS[index % STATE_COLORS.length];
                      return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                  </Pie>
                  <RechartsTooltip />
                  <Legend
                    verticalAlign="bottom"
                    content={(legendProps) => (
                      <PieLegend
                        {...legendProps}
                        payload={chart5LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart5Hidden, item)}
                        hiddenItems={chart5Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart5Hidden, chart5SeriesKeys)}
                      />
                    )}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 6: NOC Transfers (Started at NOC and not solved by automation, Solved by automation, and Sent Out) */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Incident Transfers
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chart6Data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <RechartsTooltip />
                <Legend
                  content={(legendProps) => (
                    <ScrollableLegend
                      {...legendProps}
                      payload={chart6LegendPayload}
                      onToggle={(item: string) => toggleLegendItem(setChart6Hidden, item)}
                      hiddenItems={chart6Hidden}
                      onToggleAll={() => toggleAllLegendItems(setChart6Hidden, chart6SeriesKeys)}
                    />
                  )}
                />
                {chart6SeriesKeys.map((key) => (
                  <Bar 
                    key={key}
                    dataKey={key} 
                    fill={TRANSFER_CATEGORY_COLOR_MAP[key] || TRANSFER_COLORS[0]} 
                    hide={chart6Hidden.has(key)}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 7: OBS Incidents */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Incidents Sent to OBS
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chart7Data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <RechartsTooltip />
                <Legend
                  content={(legendProps) => (
                    <ScrollableLegend
                      {...legendProps}
                      payload={chart7LegendPayload}
                      onToggle={(item: string) => toggleLegendItem(setChart7Hidden, item)}
                      hiddenItems={chart7Hidden}
                      onToggleAll={() => toggleAllLegendItems(setChart7Hidden, chart7SeriesKeys)}
                    />
                  )}
                />
                {chart7SeriesKeys.map((key) => (
                  <Bar 
                    key={key}
                    dataKey={key} 
                    fill={OBS_CATEGORY_COLOR_MAP[key] || '#0067b3'} 
                    hide={chart7Hidden.has(key)}
                    isAnimationActive={false}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Chart 10: CI Class Split */}
        <Grid item xs={12} lg={6}>
          <Paper sx={chartPaperSx}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Incidents by CI Class
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Box>
              <ToggleButtonGroup
                value={chart10Type}
                exclusive
                onChange={(e, val) => val && setChart10Type(val)}
                size="small"
              >
                <ToggleButton value="histogram">
                  <Tooltip title="Histogram">
                    <BarChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="pie">
                  <Tooltip title="Pie Chart">
                    <PieChartIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={350}>
              {chart10Type === 'histogram' ? (
                <BarChart data={chart10Data}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend
                    content={(legendProps) => (
                      <ScrollableLegend
                        {...legendProps}
                        payload={chart10LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart10Hidden, item)}
                        hiddenItems={chart10Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart10Hidden, chart10SeriesKeys)}
                      />
                    )}
                  />
                  {allCIClasses.map((cls, index) => (
                    <Bar 
                      key={cls} 
                      dataKey={cls} 
                      fill={ciClassColorMap[cls] || RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length]} 
                      hide={chart10Hidden.has(cls)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              ) : (
                <PieChart>
                  <Pie
                    data={chart10PieData}
                    cx="50%"
                    cy="40%"
                    labelLine={false}
                    label={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    isAnimationActive={false}
                  >
                    {chart10PieData.map((entry, index) => {
                      const fillColor = ciClassColorMap[entry.name] || RESOLUTION_CODE_COLORS[index % RESOLUTION_CODE_COLORS.length];
                      return <Cell key={`cell-${index}`} fill={fillColor} />;
                    })}
                  </Pie>
                  <RechartsTooltip />
                  <Legend
                    verticalAlign="bottom"
                    content={(legendProps) => (
                      <PieLegend
                        {...legendProps}
                        payload={chart10LegendPayload}
                        onToggle={(item: string) => toggleLegendItem(setChart10Hidden, item)}
                        hiddenItems={chart10Hidden}
                        onToggleAll={() => toggleAllLegendItems(setChart10Hidden, chart10SeriesKeys)}
                      />
                    )}
                  />
                </PieChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(NOCDashboardVisual);
